/* Iter 418.ak (Dirk, 2026-02-28) — AI Plant Insights panel.
   Sits under the KPI grid in the Plant Report tab. CRM only — the
   panel simply doesn't render for portal (`client`) users on the FE
   and the backend rejects them anyway.
*/
import { useState } from "react";
import {
  Sparkles, Download, TrendingUp, AlertTriangle, Lightbulb,
  ClipboardCheck, RefreshCw, Info, CheckCircle2, XCircle, AlertCircle,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";

const SEV_COLORS = {
  info:     { bar: "border-slate-400/40",   icon: Info,          fg: "text-slate-200" },
  positive: { bar: "border-emerald-500/40", icon: CheckCircle2,  fg: "text-emerald-300" },
  warning:  { bar: "border-amber-500/40",   icon: AlertTriangle, fg: "text-amber-300" },
  critical: { bar: "border-rose-500/40",    icon: XCircle,       fg: "text-rose-300" },
};

const CONFIDENCE_COLORS = {
  high:   "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  medium: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  low:    "bg-rose-500/20 text-rose-300 border-rose-500/40",
};

export default function AIInsightsPanel({ customerId, fromIso, toIso, allTime, customerName }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);

  const paramsBody = () => allTime ? { all_time: 1 } : { from: fromIso, to: toIso };

  const generate = async () => {
    setLoading(true);
    setErr("");
    setPayload(null);
    try {
      const { data } = await api.post(
        `/perf/plant-report/${customerId}/ai-insights`,
        null,
        { params: paramsBody(), timeout: 120_000 },
      );
      setPayload(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(
        `/perf/plant-report/${customerId}/ai-pdf`,
        { params: paramsBody(), responseType: "blob", timeout: 120_000 },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (customerName || customerId).replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 64);
      a.download = `HidroVerse_AIInsights_${safe}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (e) {
      alert(formatErr(e) || "PDF download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const insights = payload?.insights;
  const charts = payload?.charts || [];

  return (
    <div className="hv-card p-3 space-y-3 border-cyan-500/20 bg-gradient-to-br from-cyan-500/[0.03] to-emerald-500/[0.03]" data-testid="ai-insights-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-300" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">AI Insights</div>
            <div className="text-[10px] text-[#647087]">
              CRM-only · powered by Claude Sonnet 4.6
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={generate}
            disabled={loading}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="ai-insights-generate"
          >
            <Sparkles className={`w-3.5 h-3.5 ${loading ? "animate-pulse" : ""}`} />
            {loading ? "Analysing…" : payload ? "Re-analyse" : "Generate AI insights"}
          </button>
          {payload && (
            <button
              type="button"
              onClick={downloadPdf}
              disabled={downloading}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="ai-insights-download-pdf"
            >
              <Download className={`w-3.5 h-3.5 ${downloading ? "animate-pulse" : ""}`} />
              {downloading ? "Preparing…" : "AI PDF"}
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-6 justify-center text-sm text-cyan-300" data-testid="ai-insights-loading">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Claude is studying every reading, incident and ticket… this can take 30-90 s.</span>
        </div>
      )}

      {err && (
        <div className="border border-rose-500/40 bg-rose-500/10 rounded-md p-3 text-sm text-rose-200">
          <AlertCircle className="inline w-3.5 h-3.5 mr-1" />
          {err}
        </div>
      )}

      {!loading && !payload && !err && (
        <div className="border border-dashed border-white/10 rounded-md p-6 text-center text-[12px] text-[#a0aabf] space-y-2">
          <Sparkles className="w-6 h-6 mx-auto text-cyan-300/60" />
          <div>
            Ask Claude to read every reading, ticket and incident for this customer and
            surface what a senior operator would actually notice.
          </div>
          <div className="text-[10px] text-[#647087]">
            Claude picks its own KPIs, spots anomalies, drafts recommendations and even
            decides which charts are worth drawing.
          </div>
        </div>
      )}

      {insights && (
        <>
          {/* Headline + confidence */}
          <div className="flex items-start gap-2 p-3 border border-cyan-500/25 rounded-md bg-cyan-500/[0.04]">
            <Sparkles className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-wider text-cyan-300 mb-0.5">Bottom line</div>
              <div className="text-sm text-white/95" data-testid="ai-insights-headline">
                {insights.headline}
              </div>
            </div>
            {insights.confidence && (
              <span
                className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border ${CONFIDENCE_COLORS[insights.confidence] || CONFIDENCE_COLORS.medium}`}
                title={`Model confidence in this analysis: ${insights.confidence}`}
              >
                {insights.confidence} confidence
              </span>
            )}
          </div>

          {/* Sections */}
          {insights.sections?.length > 0 && (
            <div className="space-y-2" data-testid="ai-insights-sections">
              {insights.sections.map((s, i) => {
                const sev = SEV_COLORS[s.severity] || SEV_COLORS.info;
                const Icon = sev.icon;
                return (
                  <div
                    key={i}
                    className={`border-l-2 pl-3 py-1.5 ${sev.bar}`}
                    data-testid={`ai-section-${i}`}
                  >
                    <div className={`text-xs font-semibold flex items-center gap-1.5 ${sev.fg}`}>
                      <Icon className="w-3 h-3" /> {s.title}
                    </div>
                    <div className="text-[12px] text-[#c4cad9] mt-1 leading-snug">{s.body}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Anomalies */}
          {insights.anomalies?.length > 0 && (
            <div className="border border-amber-500/25 rounded-md p-2.5 space-y-1.5 bg-amber-500/[0.04]" data-testid="ai-insights-anomalies">
              <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Anomalies detected ({insights.anomalies.length})
              </div>
              {insights.anomalies.map((a, i) => (
                <div key={i} className="text-[12px] text-[#e2e8f0] flex gap-1.5">
                  <span className={a.severity === "critical" ? "text-rose-400" : "text-amber-400"}>◆</span>
                  <span>{a.description}</span>
                </div>
              ))}
            </div>
          )}

          {/* Charts (AI picked) */}
          {charts.length > 0 && (
            <div className="space-y-2" data-testid="ai-insights-charts">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Charts Claude picked ({charts.length})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {charts.map((c, i) => (
                  <div key={i} className="border border-white/5 rounded-md overflow-hidden bg-white" data-testid={`ai-chart-${i}`}>
                    <img
                      src={`data:image/png;base64,${c.png_b64}`}
                      alt={c.parameter_name}
                      className="w-full block"
                    />
                    <div className="p-2 bg-slate-900/95 text-white">
                      <div className="text-[11px] font-semibold">
                        {c.parameter_name} {c.unit && <span className="text-[#647087]">({c.unit})</span>}
                      </div>
                      <div className="text-[10px] text-[#a0aabf] italic mt-0.5">{c.reason}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPIs suggested */}
          {insights.kpis_suggested?.length > 0 && (
            <div className="border border-white/5 rounded-md p-2.5 bg-white/[0.02]" data-testid="ai-insights-kpis">
              <div className="text-[10px] uppercase tracking-wider text-[#22d3ee] font-bold mb-1.5 flex items-center gap-1">
                <Lightbulb className="w-3 h-3" /> KPIs Claude thinks you should track
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                {insights.kpis_suggested.map((k, i) => (
                  <div key={i} className="border border-white/5 rounded p-1.5">
                    <div className="font-semibold text-cyan-300">{k.name}</div>
                    <div className="text-[10px] text-[#a0aabf] mt-0.5"><em>Formula:</em> {k.formula}</div>
                    <div className="text-[10px] text-[#c4cad9] mt-0.5">{k.why}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {insights.recommendations?.length > 0 && (
            <div className="border border-white/5 rounded-md p-2.5" data-testid="ai-insights-recs">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1.5 flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3" /> Recommended actions
              </div>
              <div className="space-y-1.5">
                {insights.recommendations.map((r, i) => {
                  const prio = (r.priority || "medium").toLowerCase();
                  const dot = prio === "high" ? "bg-rose-500" : prio === "medium" ? "bg-amber-500" : "bg-slate-400";
                  return (
                    <div key={i} className="flex gap-2 items-start text-[12px]">
                      <span className={`inline-block w-2 h-2 mt-1.5 rounded-full shrink-0 ${dot}`} />
                      <div>
                        <span className="text-[9px] uppercase tracking-wider text-[#647087] mr-1">{prio}</span>
                        <span className="text-[#e2e8f0]">{r.action}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="text-[9px] text-[#647087] text-right">
            Generated {new Date(payload.generated_at).toLocaleString()} · {payload.model}
          </div>
        </>
      )}
    </div>
  );
}
